import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StackedToaster } from './components/atoms/StackedToaster/StackedToaster'
import { TOAST_POSITION, TOAST_BASE_STYLE } from './utils/toast'
import { ErrorBoundary } from './components/ErrorBoundary'
import { QueryProvider, queryClient } from './contexts/QueryProvider'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ReconnectBanner } from './components/ReconnectBanner'
import { usePrefetchPreferences } from './hooks/usePrefetchPreferences'
import { useNotificationSocket } from './hooks/useNotificationSocket'

// Preload icon sets locally so @iconify/react renders icons without
// depending on the Iconify API CDN (which can be slow/blocked — every
// icon missing here falls back to a per-icon network fetch).
// Solar set is TRIMMED to only the icons the app uses — regenerate with
// `node scripts/generate-solar-icons.mjs` after adding new solar:* icons.
import { addCollection } from '@iconify/react'
import bxIcons from '@iconify-json/bx/icons.json'
import solarIcons from './src/assets/iconify/solar-icons.json'
addCollection(bxIcons)
addCollection(solarIcons)

// Import the generated route tree
import { routeTree } from './src/routeTree.gen'

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {
      user: null,
      loading: true,
      isAdmin: false,
    },
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Inner app component that provides auth context to router
function InnerApp() {
  const auth = useAuth()
  const isAdmin = auth.currentUser?.email === 'brainiac@brainiacsbots.com'
  useNotificationSocket(auth.currentUser?.uid)
  // Warm the preferences cache at startup so settings/sidebar open instantly.
  usePrefetchPreferences()

  return (
    <>
      <ReconnectBanner />
      <RouterProvider
        router={router}
        context={{
          queryClient,
          auth: {
            user: auth.currentUser,
            loading: auth.loading,
            isAdmin,
          },
        }}
      />
      {/* Single toast host — dark minimalist style shared with utils/toast.ts.
          Card-deck: newest toast on top, older ones collapse into +N pill. */}
      <StackedToaster position={TOAST_POSITION} toastOptions={{ style: TOAST_BASE_STYLE }} />
    </>
  )
}

// Root element
const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Could not find root element to mount to')
}

// Render
const root = ReactDOM.createRoot(rootElement)
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryProvider>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </QueryProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
